// Sociopath developer/design preview panel — NOT production scoring logic.
// Lets product/design change the normalized report values (post
// mapSociopathReportModel) to evaluate gauges, bars, and colors while the
// real Sociopath scoring/classification model is still unimplemented (no
// nearest_profile classifier exists anywhere in this codebase yet).
//
// Flow: reportData (from mapSociopathReportModel) -> applyDevOverrides ->
// JSX. Never touches the API; only selects among band/category text that
// already exists verbatim in the real content payload, via
// computeProvisionalSociopathClassification (assessment-utils.js) — the
// same provisional classifier the base report and the dashboard overview
// card use, so a slider move, the real result, and the dashboard always
// agree on how a given percentage maps to a category.
//
// Disposable by design — delete this dev-tools/ directory, its import in
// component.js, and the unwrapSociopathContent export in report-model.js to
// remove the feature entirely.

import React, { useState } from "react";
import {
  computeProvisionalSociopathClassification,
  PROVISIONAL_SOCIOPATH_CLASSIFICATION_BUCKETS,
} from "../../../utils/assessment-utils";
import { LOCALE } from "../../../utils/constants";

const FACTOR_LABELS = {
  boldness: "Boldness",
  meanness: "Meanness",
  disinhibition: "Disinhibition",
};

// Dev Controls language switcher options. Selecting one navigates to this
// report's real locale-aware route (see component.js's
// handleDevLocaleChange) — not local UI state, so content, footer, and
// i18next all follow the normal app localization path afterward.
const LANGUAGE_OPTIONS = [
  { code: LOCALE.EN, label: "English" },
  { code: LOCALE.ES, label: "Spanish" },
  { code: LOCALE.FR, label: "French" },
  { code: LOCALE.JA, label: "Japanese" },
  { code: LOCALE.IT, label: "Italian" },
];

export const EMPTY_DEV_OVERRIDES = {
  factors: {
    boldness: { percentage: null },
    meanness: { percentage: null },
    disinhibition: { percentage: null },
  },
  overall: { score: null },
};

const NAMED_DEV_PRESETS = {
  low: { boldness: 15, meanness: 15, disinhibition: 20, overall: 15 },
  mixed: { boldness: 45, meanness: 55, disinhibition: 50, overall: 50 },
  high: { boldness: 85, meanness: 90, disinhibition: 80, overall: 88 },
};

// Buttons for the top "Presets" row — keep this list separate from the
// boundary-test keys below so that row doesn't get cluttered with numeric
// threshold preset names.
export const DEV_PRESET_NAMES = Object.keys(NAMED_DEV_PRESETS);

// One entry per real transition between adjacent bands in
// PROVISIONAL_SOCIOPATH_CLASSIFICATION_BUCKETS (assessment-utils.js) — the
// same buckets computeProvisionalSociopathClassification uses for both
// factor levels and the overall category, so these are the actual scoring
// boundaries, not guessed round numbers. The last bucket's `max` (100) is
// the scale ceiling, not a transition, so it's excluded.
const CLASSIFICATION_BOUNDARIES = PROVISIONAL_SOCIOPATH_CLASSIFICATION_BUCKETS.slice(
  0,
  -1
).map((bucket, index) => ({
  threshold: bucket.max,
  fromBand: bucket.shortLabel,
  toBand: PROVISIONAL_SOCIOPATH_CLASSIFICATION_BUCKETS[index + 1].shortLabel,
}));

// Sets every factor AND the overall score to the same value, so a single
// button click puts the whole report (factor bands + overall category) on
// one deterministic, easy-to-read point relative to a boundary.
const boundaryPresetValues = (value) => ({
  boldness: value,
  meanness: value,
  disinhibition: value,
  overall: value,
});

// For each real threshold, one preset just below it, one exactly at it, and
// one just above it — e.g. a threshold of 24 yields 23 / 24 / 25. Grouped by
// boundary (rather than a flat list) so the panel can render each boundary's
// three buttons together under a label naming the transition it tests.
export const DEV_BOUNDARY_PRESETS = CLASSIFICATION_BOUNDARIES.map(
  ({ threshold, fromBand, toBand }) => ({
    threshold,
    fromBand,
    toBand,
    below: { key: `boundary_${threshold}_below`, value: threshold - 1 },
    at: { key: `boundary_${threshold}_at`, value: threshold },
    above: { key: `boundary_${threshold}_above`, value: threshold + 1 },
  })
);

// handleDevPreset in component.js looks up presets generically as
// DEV_PRESETS[name], so the boundary keys are merged into the same
// dictionary it already reads from — no component.js changes needed.
export const DEV_PRESETS = {
  ...NAMED_DEV_PRESETS,
  ...Object.fromEntries(
    DEV_BOUNDARY_PRESETS.flatMap(({ below, at, above }) => [
      [below.key, boundaryPresetValues(below.value)],
      [at.key, boundaryPresetValues(at.value)],
      [above.key, boundaryPresetValues(above.value)],
    ])
  ),
};

// Environments where the dev panel is allowed to appear at all. Explicit
// allowlist rather than `!== "PRODUCTION"` — an unknown/missing/misspelled
// REACT_APP_ENVIRONMENT value must fail closed, not accidentally pass.
const DEV_PREVIEW_ALLOWED_ENVIRONMENTS = ["DEVELOPMENT", "QA", "STAGING"];

// Gate: requires ALL THREE — an allowlisted lower-environment build,
// the explicit REACT_APP_ENABLE_REPORT_DEV_TOOLS=true flag, AND the
// ?reportDebug=1 query flag.
//
// Production remains fail-closed in two ways:
// 1. PRODUCTION is not in DEV_PREVIEW_ALLOWED_ENVIRONMENTS.
// 2. .env.PRODUCTION explicitly sets REACT_APP_ENABLE_REPORT_DEV_TOOLS=false.
//
// Unknown or malformed environment values also fail closed.
export const isDevPreviewAllowed = (queryParams) =>
  DEV_PREVIEW_ALLOWED_ENVIRONMENTS.includes(process.env.REACT_APP_ENVIRONMENT) &&
  process.env.REACT_APP_ENABLE_REPORT_DEV_TOOLS === "true" &&
  queryParams.get("reportDebug") === "1";

// Pure function: layers `overrides` onto an already-normalized `reportData`
// (the mapSociopathReportModel output), re-reading label/description text
// for the resulting band/category from the real content payload — never
// inventing new copy.
export const applyDevOverrides = (reportData, overrides, content) => {
  const factors = reportData.factors.map((factor) => {
    const percentage = overrides.factors[factor.key]?.percentage;
    if (percentage == null) return factor;

    const level = computeProvisionalSociopathClassification(percentage).band;
    const bandContent = content.factorBreakdown.factors[factor.key]?.bands?.[level];
    return {
      ...factor,
      percentage,
      level,
      levelLabel: bandContent?.label ?? factor.levelLabel,
      body: bandContent?.description ?? factor.body,
    };
  });

  let overallResult = reportData.overallResult;
  const overallScore = overrides.overall.score;
  if (overallScore != null) {
    const classification = computeProvisionalSociopathClassification(overallScore);
    const category = content.categoryResult.categories[classification.categoryKey];
    overallResult = {
      ...overallResult,
      score: overallScore,
      // Same localized-backend-label rule as the base (non-overridden)
      // path in report-model.js — shortLabel preferred, falling back to
      // the full label, and only falling back to the prior value if this
      // override's category has neither. Never classification.shortLabel.
      classification:
        (category?.shortLabel || category?.label) ?? overallResult.classification,
      band: classification.band,
      classificationLabel: category?.label ?? overallResult.classificationLabel,
      paragraphs: category?.paragraphs ?? overallResult.paragraphs,
    };
  }

  return {
    ...reportData,
    factors,
    overallResult: {
      ...overallResult,
      scoresTable: [
        ...factors.map((factor) => ({
          label: factor.title,
          value: `${factor.percentage} / ${factor.visualMaxScore} · ${factor.levelLabel}`,
          band: factor.level,
        })),
        {
          label: "Classification",
          labelKey: "report.sociopath.classification",
          value: overallResult.classificationLabel,
          band: overallResult.band,
        },
      ],
    },
  };
};

const Slider = ({ label, value, onChange }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-3">
    <div className="flex justify-between mb-1">
      <span>{label}</span>
      <span className="text-gray-400">{value ?? "—"}</span>
    </div>
    <input
      type="range"
      min={0}
      max={100}
      value={value ?? 50}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-indigo-600"
    />
  </label>
);

const ReportDebugPanel = ({
  overrides,
  onFactorChange,
  onOverallChange,
  onPreset,
  onReset,
  currentLocale,
  onLocaleChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {isOpen && (
        // Capped to the viewport (minus room for the bottom-6 inset, the
        // mb-3 gap, and the w-12/h-12 toggle button below) and split into a
        // flex-shrink-0 header + a min-h-0/overflow-y-auto body, so a panel
        // taller than the screen scrolls internally instead of extending
        // off-screen with no way back to the top controls — the ⚙ toggle
        // and "Dev Controls"/"Preview only" header stay reachable at all
        // times. On any viewport tall enough for the panel's natural
        // height, max-h never binds and nothing scrolls — desktop/tablet
        // render exactly as before.
        <div className="mb-3 w-72 max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-5 mb-4 flex-shrink-0">
            <h4 className="font-bold text-sm text-gray-800">Dev Controls</h4>
            <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 rounded-full px-2 py-1">
              Preview only
            </span>
          </div>

          <div className="min-h-0 overflow-y-auto overscroll-contain px-5 pb-5">
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-600 mb-1">Language</div>
              <div className="flex flex-wrap gap-1.5">
                {LANGUAGE_OPTIONS.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => onLocaleChange(option.code)}
                    className={`text-xs font-semibold rounded-lg px-2.5 py-1 ${
                      currentLocale === option.code
                        ? "bg-indigo-600 text-white"
                        : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {Object.keys(FACTOR_LABELS).map((key) => (
              <Slider
                key={key}
                label={FACTOR_LABELS[key]}
                value={overrides.factors[key].percentage}
                onChange={(value) => onFactorChange(key, value)}
              />
            ))}
            <Slider label="Overall" value={overrides.overall.score} onChange={onOverallChange} />

            <div className="flex gap-2 mt-2 mb-3">
              {DEV_PRESET_NAMES.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => onPreset(name)}
                  className="flex-1 text-xs font-semibold capitalize bg-indigo-50 text-indigo-700 rounded-lg py-1.5 hover:bg-indigo-100"
                >
                  {name}
                </button>
              ))}
            </div>

            <div className="mb-3">
              <div className="text-xs font-semibold text-gray-600 mb-1">
                Boundary tests
              </div>
              {DEV_BOUNDARY_PRESETS.map(({ threshold, fromBand, toBand, below, at, above }) => (
                <div key={threshold} className="mb-1.5">
                  <div className="text-[10px] text-gray-400 mb-0.5">
                    {fromBand} → {toBand} at {threshold}
                  </div>
                  <div className="flex gap-1.5">
                    {[below, at, above].map((preset) => (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() => onPreset(preset.key)}
                        className="flex-1 text-xs font-semibold bg-gray-50 text-gray-700 rounded-lg py-1 hover:bg-gray-100"
                      >
                        {preset.value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={onReset}
              className="w-full text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg py-1.5 hover:bg-gray-50"
            >
              Reset to source result
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="w-12 h-12 rounded-full bg-indigo-600 text-white shadow-lg flex items-center justify-center text-lg font-bold hover:bg-indigo-700"
        title="Dev Controls"
      >
        {isOpen ? "×" : "⚙"}
      </button>
    </div>
  );
};

export default ReportDebugPanel;
